import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import {
  getUnmutedId,
  nextVideoId,
  setUnmutedId,
  subscribeUnmuted,
} from '../../utilities/videoCoordinator';

/**
 * Shared video element for feed cards, detail pages, and the AddPost preview.
 *
 * Behaviors:
 *   - Autoplay when ≥50% in viewport, pause when scrolled out. Reusing native
 *     `autoPlay` alone fires every video at mount; the IntersectionObserver
 *     gate is what keeps a long feed from going wild.
 *   - Default muted. Unmute is a single global slot via `videoCoordinator`:
 *     when one VideoPlayer unmutes, every other one subscribes and re-mutes.
 *   - Tap the play/pause button to toggle playback; the underlying autoplay
 *     resumes once the user scrolls out and back in (intentional — same as
 *     Instagram / Twitter).
 *
 * `src` is the asset URL. `objectFit` defaults to `cover` (good for the feed
 * grid); detail page passes `contain` so portrait videos aren't cropped.
 */
export function VideoPlayer({ src, className = '', objectFit = 'cover', preview = false }) {
  const intl = useIntl();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const idRef = useRef(null);
  if (!idRef.current) idRef.current = nextVideoId();

  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(true);

  // Autoplay. In `preview` mode (AddPost) the video should just play as soon as
  // it can — no visibility gate. In feed mode it plays only while ≥50% visible
  // and pauses when scrolled out. The play() promise rejects silently if the
  // browser blocks autoplay; we don't fight it.
  useEffect(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!video) return;
    // iOS (and React) gotcha: iOS only autoplays muted videos when the `muted`
    // ATTRIBUTE is present, but React sets the muted *property* not the
    // attribute. Set both explicitly so a freshly mounted video autoplays.
    // Runtime unmute still works via the muted property.
    video.muted = true;
    video.setAttribute('muted', '');
    const play = () => video.play().catch(() => null);

    if (preview) {
      // Always autoplay the preview as soon as it has data.
      if (video.readyState >= 2) play();
      video.addEventListener('loadeddata', play);
      return () => video.removeEventListener('loadeddata', play);
    }

    if (!container) return;
    let visible = false;
    const tryPlay = () => {
      if (visible) play();
    };
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          visible = e.isIntersecting;
          if (visible) tryPlay();
          else video.pause();
        }
      },
      { threshold: 0.5 },
    );
    obs.observe(container);
    // The src may not be ready when the observer first fires; retry on load
    // (gated by visibility so off-screen feed videos stay paused).
    video.addEventListener('loadeddata', tryPlay);
    return () => {
      obs.disconnect();
      video.removeEventListener('loadeddata', tryPlay);
    };
  }, [src, preview]);

  // Mirror the element's play/pause into React state so the toggle button
  // reflects what the user actually sees.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlay = () => setPaused(false);
    const onPause = () => setPaused(true);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, []);

  // Force-mute whenever someone else claims the unmuted slot. On unmount,
  // release the slot if we owned it — otherwise the singleton holds a
  // stale id forever and the next video that subscribes silently mutes
  // itself thinking somebody else is unmuted.
  useEffect(() => {
    const unsubscribe = subscribeUnmuted((unmutedId) => {
      if (unmutedId !== idRef.current) {
        setMuted(true);
      }
    });
    return () => {
      unsubscribe();
      if (getUnmutedId() === idRef.current) {
        setUnmutedId(null);
      }
    };
  }, []);

  const togglePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => null);
    } else {
      video.pause();
    }
  };

  const toggleMute = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (muted) {
      setMuted(false);
      setUnmutedId(idRef.current);
    } else {
      setMuted(true);
      if (getUnmutedId() === idRef.current) setUnmutedId(null);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <video
        ref={videoRef}
        src={src}
        muted={muted}
        loop
        playsInline
        autoPlay
        className="w-full h-full"
        style={{ objectFit }}
      />
      {/* Pointer-events on the inner buttons only so taps on the video
          surface don't get swallowed by the overlay. */}
      <div className="absolute inset-0 flex items-end justify-between p-2 pointer-events-none">
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted
            ? intl.formatMessage({ id: 'video.aria.unmute', defaultMessage: 'Unmute' })
            : intl.formatMessage({ id: 'video.aria.mute', defaultMessage: 'Mute' })}
          className="pointer-events-auto bg-black/60 hover:bg-black/80 text-white rounded-full w-8 h-8 flex items-center justify-center"
        >
          {muted ? (
            <VolumeOffIcon style={{ fontSize: 18 }} />
          ) : (
            <VolumeUpIcon style={{ fontSize: 18 }} />
          )}
        </button>
        <button
          type="button"
          onClick={togglePlay}
          aria-label={paused
            ? intl.formatMessage({ id: 'video.aria.play', defaultMessage: 'Play' })
            : intl.formatMessage({ id: 'video.aria.pause', defaultMessage: 'Pause' })}
          className="pointer-events-auto bg-black/60 hover:bg-black/80 text-white rounded-full w-8 h-8 flex items-center justify-center"
        >
          {paused ? (
            <PlayArrowIcon style={{ fontSize: 18 }} />
          ) : (
            <PauseIcon style={{ fontSize: 18 }} />
          )}
        </button>
      </div>
    </div>
  );
}
